import { PayloadAction } from '@reduxjs/toolkit';
import { FetchError, toLoggableError } from '@yandex-360/telemost-sdk';
import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { call, cancel, cancelled, fork, put, race, select, take } from 'typed-redux-saga';

import { ErrorPageType } from '../../../../../shared/components/error-page/types';
import { setErrorPageAction } from '../../../../../shared/features/config';
import { selectTelemostHost } from '../../../../../shared/features/config/selectors';
import { poll } from '../../../../../shared/helpers/redux-saga';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { handleFetchError } from '../../../helpers/handlers-sagas';
import { PollWaitingRoomPayload } from '../../../types';
import { disconnectedFromConferenceAction } from '../../conference/slice';
import { logger } from '../helpers/logger';
import {
    pollingWaitingRoomFailedAction,
    pollingWaitingRoomSuccessAction,
    startPollWaitingRoomAccessAction,
} from '../slice';

export function* watchWaitingRoomAccessPollingSaga() {
    let lastTask = null;
    while (true) {
        const { startPolling } = yield* race({
            startPolling: take(startPollWaitingRoomAccessAction),
            disconnected: take(disconnectedFromConferenceAction),
        });

        if (lastTask) {
            yield* cancel(lastTask);
        }
        if (startPolling) {
            lastTask = yield* fork(pollWaitingRoomAccessSaga, startPolling as PayloadAction<PollWaitingRoomPayload>);
        }
    }
}
export function* pollWaitingRoomAccessSaga({ payload: { roomId, config } }: PayloadAction<PollWaitingRoomPayload>) {
    const pollInterval = config.client_configuration?.conference_check_access_interval_ms || 3000;

    const telemostHost = yield* select(selectTelemostHost);
    const confUri = new URL(`https://${telemostHost}/j/${roomId}`);
    const fiveHundredErrorsCounter = { value: 0 };
    const leaveWaitingRoom = () => cloudApi.leaveWaitingRoom(confUri.toString());
    window.addEventListener('beforeunload', leaveWaitingRoom);

    try {
        yield* poll(
            {
                pollingInterval: pollInterval,
                onSuccess: function* (data: CloudAPI.WaitingRoomCheckAccess.Response) {
                    fiveHundredErrorsCounter.value = 0;
                    if (data.admitted) {
                        window.removeEventListener('beforeunload', leaveWaitingRoom);
                        yield* put(pollingWaitingRoomSuccessAction());
                        return true;
                    }
                },
                onError: function* (error: unknown) {
                    const shouldTerminate = yield* call(handleWaitingRoomAccessErrorsSaga, error);
                    if (shouldTerminate) {
                        yield* put(pollingWaitingRoomFailedAction);
                        return true;
                    }
                },
            },
            function* () {
                return yield* call([cloudApi, cloudApi.waitingRoomCheckAccess], confUri.toString());
            }
        );
    } catch (error) {
        logger.error(toLoggableError(error), {
            method: 'pollWaitingRoomAccessSaga',
        });
    } finally {
        if (yield* cancelled()) {
            window.removeEventListener('beforeunload', leaveWaitingRoom);
        }
    }

    function* handleWaitingRoomAccessErrorsSaga(error: unknown) {
        logger.error(toLoggableError(error), {
            method: 'handleWaitingRoomAccessErrorsSaga',
        });
        const isWaitingRoomFetchError = yield* call(handleFetchAccessError, error);
        if (isWaitingRoomFetchError) {
            return isWaitingRoomFetchError;
        }
        const isFetchError = yield* call(handleFetchError, error);
        if (isFetchError) {
            return isFetchError;
        }
        if (FetchError.is(error) && error.status >= 500) {
            fiveHundredErrorsCounter.value += 1;
        }
        if (fiveHundredErrorsCounter.value > 2) {
            yield* put(
                setErrorPageAction({
                    type: ErrorPageType.DEFAULT,
                    reason: {
                        method: 'handleWaitingRoomAccessErrorsSaga',
                        message: 'Polling stopped due to receiving errors with status 500',
                        code: FetchError.is(error) ? error.status : undefined,
                    },
                })
            );
            return true;
        }
        return false;
    }
}

enum WaitingRoomAccessErrors {
    PEER_NOT_FOUND = 'TelemostPeerNotFoundError',
    PEER_SESSION_NOT_FOUND = 'TelemostWaitingRoomPeerSessionNotFoundError',
    WRONG_STATE = 'TelemostWaitingRoomPeerSessionWrongStateError',
    TOO_MANY_PEERS = 'TelemostTooManyPeersInWaitingRoomError',
}

function* handleFetchAccessError(error: unknown) {
    if (FetchError.is(error)) {
        if (error.status === 404) {
            if (
                error.error === WaitingRoomAccessErrors.PEER_NOT_FOUND ||
                error.error === WaitingRoomAccessErrors.PEER_SESSION_NOT_FOUND
            ) {
                yield* put(
                    setErrorPageAction({
                        type: ErrorPageType.DEFAULT,
                        reason: {
                            method: 'handleFetchAccessError',
                            name: error.error,
                            code: error.status,
                        },
                    })
                );
                return true;
            }
        } else if (error.status === 409) {
            if (error.error === WaitingRoomAccessErrors.WRONG_STATE) {
                yield* put(
                    setErrorPageAction({
                        type: ErrorPageType.DEFAULT,
                        reason: {
                            method: 'handleFetchAccessError',
                            name: error.error,
                            code: error.status,
                        },
                    })
                );
            }
            if (error.error === WaitingRoomAccessErrors.TOO_MANY_PEERS) {
                yield* put(setErrorPageAction({ type: ErrorPageType.LIMIT_USERS_COUNT }));
            }
            return true;
        }
    }
    return false;
}
